import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  validateImageSize,
  IMAGE_PROCESSING_ERRORS,
  normalizeImage,
  validateImageFile,
} from '../utils/image';
import useNotificationMessage from '../../../hooks/useNotificactionMessage';
import {
  MAX_FILE_SIZE_MB,
  NOTIFICATION_TYPES,
} from '../../../config/consts/commons';

const IMAGE_PROCESSING_ERROR_CONFIG = {
  [IMAGE_PROCESSING_ERRORS.MAX_FILE_SIZE]: {
    key: 'MAX_FILE_SIZE_ERROR',
    params: { maxFileSize: MAX_FILE_SIZE_MB },
  },
  [IMAGE_PROCESSING_ERRORS.COMPRESS_FAILED]: {
    key: 'COMPRESS_IMAGE_ERROR',
    params: {},
  },
  [IMAGE_PROCESSING_ERRORS.MEMORY_ERROR]: {
    key: 'COMPRESS_IMAGE_MEMORY_ERROR',
    params: {},
  },
  [IMAGE_PROCESSING_ERRORS.CORRUPT_ERROR]: {
    key: 'COMPRESS_IMAGE_FORMAT_ERROR',
    params: {},
  },
  [IMAGE_PROCESSING_ERRORS.UNSUPPORTED_ERROR]: {
    key: 'FORMAT_IMAGE_ERROR',
    params: {},
  },
};

const getCompressionErrorConfig = (message) =>
  IMAGE_PROCESSING_ERROR_CONFIG[message] ??
  IMAGE_PROCESSING_ERROR_CONFIG[IMAGE_PROCESSING_ERRORS.COMPRESS_FAILED];

const useImageProcessor = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation(['system']);
  const { showNotificationMessage } = useNotificationMessage();

  const reset = useCallback(() => {
    setIsProcessing(false);
  }, []);

  const processImage = useCallback(
    async (file) => {
      setIsProcessing(true);

      try {
        const isValid = await validateImageFile({ file });

        if (!isValid) {
          showNotificationMessage(
            t('FORMAT_IMAGE_ERROR', { ns: 'system' }),
            NOTIFICATION_TYPES.ERROR
          );
          return null;
        }

        const normalizedFile = await normalizeImage(file);
        const {
          file: processed,
          success,
          message,
        } = await validateImageSize({ file: normalizedFile });

        if (!success) {
          const { key, params } = getCompressionErrorConfig(message);
          showNotificationMessage(
            t(key, { ns: 'system', ...params }),
            NOTIFICATION_TYPES.ERROR
          );
          return null;
        }

        return processed;
      } catch (error) {
        console.error('PROCESS_IMAGE_UNEXPECTED_ERROR', error);

        const { key, params } = getCompressionErrorConfig(error.message);

        showNotificationMessage(
          t(key, { ns: 'system', ...params }),
          NOTIFICATION_TYPES.ERROR
        );
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    [t, showNotificationMessage]
  );

  return { processImage, isProcessing, reset };
};

export default useImageProcessor;
